function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState } from 'react';
import { CustomModal, Button, TableList, Page, Spinner, PageErrorState, PageContentHeader } from 'seagull';
import { useStore, useStoreActions } from "../../../store/store-context";
import { modalSize } from "../../../infra/modals";
import { sendErrReport } from "../../../infra/utils";
import { useOnMount } from "../../../hooks/useOnMount";
import { useEventReporter } from "../../../event-reporter";
import { selectors } from "../../../store/state-selectors";
import { MapAccountsRow } from "./MapAccountsRow";
import { columnDefs, headerItems } from "./MapAccountsModal.utils";
import styles from "./MapAccountsModal.module.scss";
export function MapAccountsModal(_ref) {
  var _qboIntegration$accou, _qboIntegration$accou2, _qboIntegration$accou3;
  let {
    dismissModal
  } = _ref;
  const {
    api,
    logBI,
    fetchIntegrations
  } = useStoreActions();
  const {
    reportToHeap
  } = useEventReporter();
  const qboIntegration = useStore(selectors.integration.qboIntegration);
  const [modalState, setModalState] = useState('loading');
  const [qboAccounts, setQboAccounts] = useState();
  const userInfo = useStore(state => state.userInfo);
  async function fetchAccounts() {
    setModalState('loading');
    try {
      const data = await api.qbo.getAccounts();
      setQboAccounts(data);
      setModalState('valid');
    } catch (err) {
      setModalState('error');
    }
  }
  async function _onLink(linkType, account) {
    const isPaymentAccount = linkType !== 'expense';
    const biEventCode = isPaymentAccount ? 12011 : 12012;
    logBI(biEventCode, {
      vendorId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.businessId(),
      userId: userInfo === null || userInfo === void 0 ? void 0 : userInfo.id()
    });
    const heapEventName = isPaymentAccount ? 'qbo-select-payments-account' : 'qbo-select-anchor-fees-account';
    reportToHeap('integrations', 'click', heapEventName);
    try {
      await api.qbo.linkAccount({
        linkType,
        integratedId: account === null || account === void 0 ? void 0 : account.id
      });
      await fetchIntegrations();
    } catch (error) {
      sendErrReport('Failed to link service template', error);
      throw error;
    }
  }
  useOnMount(() => {
    fetchAccounts();
  });
  return /*#__PURE__*/React.createElement(CustomModal, _extends({
    "data-testid": "qbo-map-accounts-modal",
    onClose: dismissModal
  }, modalSize.large, {
    footer: /*#__PURE__*/React.createElement("div", {
      className: styles.footer
    }, /*#__PURE__*/React.createElement(Button, {
      onClick: dismissModal,
      "data-testid": "qbo-map-accounts-close"
    }, "Done"))
  }), /*#__PURE__*/React.createElement(TableList.Provider, {
    columnDefs: columnDefs
  }, /*#__PURE__*/React.createElement(Page, {
    height: "100%",
    header: "QuickBooks Online Accounts",
    subheader: "Choose accounts to map payments and fees.",
    minWidth: "880px",
    skin: "secondary"
  }, modalState === 'loading' && /*#__PURE__*/React.createElement(LoadingState, null), modalState === 'error' && /*#__PURE__*/React.createElement(ErrorState, {
    onRefresh: fetchAccounts
  }), modalState === 'valid' && qboAccounts && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageContentHeader, null, /*#__PURE__*/React.createElement(TableList.Header, {
    items: headerItems
  })), /*#__PURE__*/React.createElement(MapAccountsRow, {
    anchorAccount: "Bank account for deposited funds",
    tooltip: "Funds that have cleared and been deposited into your bank account.",
    selectedAccountId: qboIntegration === null || qboIntegration === void 0 || (_qboIntegration$accou = qboIntegration.accounts.bank) === null || _qboIntegration$accou === void 0 || (_qboIntegration$accou = _qboIntegration$accou.synced) === null || _qboIntegration$accou === void 0 ? void 0 : _qboIntegration$accou.integratedId,
    accounts: qboAccounts.bank,
    onLink: account => _onLink('bank', account),
    "data-testid": "qbo-map-bank-account-row",
    noOptionsText: "No Savings or Checking accounts to show"
  }), /*#__PURE__*/React.createElement(MapAccountsRow, {
    anchorAccount: "Expense account for merchant fees",
    tooltip: "Anchor fees and credit card fees (if not paid by clients). Anchor recommends using a dedicated account.",
    selectedAccountId: qboIntegration === null || qboIntegration === void 0 || (_qboIntegration$accou2 = qboIntegration.accounts.expense) === null || _qboIntegration$accou2 === void 0 || (_qboIntegration$accou2 = _qboIntegration$accou2.synced) === null || _qboIntegration$accou2 === void 0 ? void 0 : _qboIntegration$accou2.integratedId,
    accounts: qboAccounts.expense,
    onLink: account => _onLink('expense', account),
    "data-testid": "qbo-map-expense-account-row"
  }), /*#__PURE__*/React.createElement(MapAccountsRow, {
    anchorAccount: "Clearing account for collected funds",
    tooltip: "Funds collected and not yet deposited into your bank account.",
    selectedAccountId: qboIntegration === null || qboIntegration === void 0 || (_qboIntegration$accou3 = qboIntegration.accounts.clearing) === null || _qboIntegration$accou3 === void 0 || (_qboIntegration$accou3 = _qboIntegration$accou3.synced) === null || _qboIntegration$accou3 === void 0 ? void 0 : _qboIntegration$accou3.integratedId,
    accounts: qboAccounts.clearing,
    onLink: account => _onLink('clearing', account),
    "data-testid": "qbo-map-clearing-account-row"
  })))));
}
const LoadingState = () => /*#__PURE__*/React.createElement("div", {
  className: styles.loading
}, /*#__PURE__*/React.createElement(Spinner, {
  size: 40,
  "data-testid": "map-accounts-loading"
}));
const ErrorState = _ref2 => {
  let {
    onRefresh
  } = _ref2;
  return /*#__PURE__*/React.createElement(PageErrorState, {
    "data-testid": "map-accounts-error-state",
    message: "Failed to retrieve the relevant data, please try again later",
    onRefresh: onRefresh,
    skin: "secondary"
  });
};